/**
 * The refresh state of one part of a team's data, for its section and its
 * setup window (shipcast-o7j.3/.5). The panel that renders it is
 * `TeamRefreshPanel`.
 */

import { useDatasets, useLatestTeamRefresh, useStartTeamRefresh, useTeamTrackerState } from '../hooks';
import type { ApplyPart, TeamRefreshResponse } from '../types';

/** What a section heading should flag about its last refresh, if anything. */
export type RefreshAttention = 'error' | 'warnings' | null;

export function refreshAttention(latest: TeamRefreshResponse | null | undefined): RefreshAttention {
  if (latest?.status === 'error') return 'error';
  if (latest?.status === 'ok' && latest.warnings.length > 0) return 'warnings';
  return null;
}

export interface TeamRefresh {
  teamId: string;
  part: ApplyPart;
  /** The latest refresh of this part; `null` = never, `undefined` = loading. */
  latest: TeamRefreshResponse | null | undefined;
  /** The team's dataset name; `null` = the team has none. */
  datasetName: string | null | undefined;
  running: boolean;
  start: () => void;
  startError: Error | null;
}

/**
 * The refresh state of one part, for a section that is always mounted: the
 * poll and the `onApplied` reload happen here, whether or not its window is
 * open — a refresh can finish after the window was closed.
 */
export function useTeamRefresh(teamId: string, part: ApplyPart, onApplied?: () => void): TeamRefresh {
  const { data: latest } = useLatestTeamRefresh(teamId, part, { onOk: onApplied });
  const start = useStartTeamRefresh();
  const { data: state } = useTeamTrackerState(teamId);
  const { data: datasets } = useDatasets();

  const datasetId = state === undefined ? undefined : (state.dataset_id ?? null);
  const datasetName =
    datasetId === undefined || datasetId === null
      ? datasetId
      : (datasets?.find((d) => d.id === datasetId)?.name ?? undefined);

  return {
    teamId,
    part,
    latest,
    datasetName,
    running: start.isPending || latest?.status === 'running',
    start: () => start.mutate({ simulationTeamId: teamId, part }),
    startError: start.error instanceof Error ? start.error : null,
  };
}
